import { Routes, Route } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Package, Search, Loader2, Smartphone, Lock, Download, CheckCircle2, AlertCircle, MapPin, Clock, Sun, Moon, Truck } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { RouteMapCard, RoutePoint } from '@/components/ui/RouteMap';
import { useGeocode } from '@/hooks/useGeocode';

const API = import.meta.env.VITE_API_BASE_URL ?? 'https://backend.shiproute.demo/v1';
const api = axios.create({ baseURL: API, timeout: 25000 });

interface TrackEvent {
  id: number; statusId?: number; eventDate: string; status?: string; description?: string; location?: string; courierStatusCode?: string; city?: string; state?: string;
}
interface TrackResult {
  order: any;
  merchant?: { companyName: string; brandName?: string; logoUrl?: string };
  courier?: { name: string; brand?: string; logoUrl?: string };
  events: TrackEvent[];
}

// Small colored highlight per tracking status — Delivered → green, Delivery Failed/RTO → red, etc.
const PUBLIC_STATUS_TONE: Record<number, string> = {
  1: 'bg-blue-100 text-blue-800', 2: 'bg-blue-100 text-blue-800',
  3: 'bg-amber-100 text-amber-800', 4: 'bg-amber-100 text-amber-800', 5: 'bg-amber-100 text-amber-800',
  6: 'bg-indigo-100 text-indigo-800', 7: 'bg-indigo-100 text-indigo-800',
  8: 'bg-emerald-100 text-emerald-800', 12: 'bg-emerald-100 text-emerald-800', 19: 'bg-emerald-100 text-emerald-800',
  9: 'bg-rose-100 text-rose-800', 13: 'bg-rose-100 text-rose-800', 14: 'bg-rose-100 text-rose-800',
  10: 'bg-violet-100 text-violet-800', 11: 'bg-violet-100 text-violet-800', 18: 'bg-violet-100 text-violet-800',
  17: 'bg-amber-100 text-amber-800',
};
/** `2026-08-14T…` → `14 Aug 2026`. Dates only — the customer never needs a timestamp on an estimate. */
const fmtDate = (d: string | Date) =>
  new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

// Fall back to keyword matching when an event has no numeric statusId.
function eventTone(e: TrackEvent): string {
  if (e.statusId && PUBLIC_STATUS_TONE[e.statusId]) return PUBLIC_STATUS_TONE[e.statusId];
  const t = `${e.description ?? ''} ${e.status ?? ''}`.toLowerCase();
  if (/fail|undeliver|rto|return|lost|damage/.test(t)) return 'bg-rose-100 text-rose-800';
  if (/deliver/.test(t)) return 'bg-emerald-100 text-emerald-800';
  return 'bg-slate-100 text-slate-700';
}

/**
 * The waybill carried by the link a customer tapped, if any.
 *
 * Canonical form is the query string — `https://track.example.com?awb_no=X1011430427` — because a
 * B2B consignment is tracked by an LR that contains slashes (`GNT/AWB/123456`), which cannot survive a
 * path segment. `?awb=` and `?q=` are accepted as aliases, and the older path form (`/X1011430427`) is
 * still honoured so the links already sent out in SMS keep working.
 */
function waybillFromUrl(): string {
  try {
    const p = new URLSearchParams(window.location.search);
    const fromQuery = p.get('awb_no') ?? p.get('awb') ?? p.get('q');
    if (fromQuery?.trim()) return fromQuery.trim();
    // Everything after the leading slash — kept whole, so a multi-segment LR survives. The
    // `/track/` prefix is dropped first: this page is reachable both as a whole host (`trk.*`,
    // where the path IS the waybill) and as a path on the panel's own host, and without this the
    // panel form would look up a waybill called "track/DMO70000031676".
    return decodeURIComponent(window.location.pathname)
      .replace(/^\/+/, '')
      .replace(/^track\/?/i, '')
      .replace(/\/+$/, '')
      .trim();
  } catch {
    return '';
  }
}

function TrackHome() {
  const [q, setQ] = useState(waybillFromUrl);
  const [mobile, setMobile] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<TrackResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Theme — defaults to LIGHT; persisted in localStorage as 'gl-trk-theme'. Deliberately NOT tied to
  // the device's colour scheme: this page is opened from a delivery SMS by someone with no account,
  // and it should look the same for all of them. The panel-wide ThemeProvider is disabled on this
  // host (see utils/appHost) so nothing else can repaint the root behind us.
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    try { return (localStorage.getItem('gl-trk-theme') as 'light' | 'dark') || 'light'; }
    catch { return 'light'; }
  });
  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    document.documentElement.setAttribute('data-theme', theme);
    try { localStorage.setItem('gl-trk-theme', theme); } catch {}
  }, [theme]);

  const search = async (term?: string) => {
    const query = (term ?? q).trim();
    if (!query) return toast.error('Enter AWB / LR / Order number');
    setBusy(true); setError(null); setResult(null);
    try {
      const r = await api.get<TrackResult>('/public/track', { params: { q: query, mobile: mobile.trim() || undefined } });
      setResult(r.data);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message ?? 'Lookup failed');
    } finally {
      setBusy(false);
    }
  };

  // Landed on a link that already names the shipment → track it straight away. The customer tapped a
  // link about ONE parcel; making them press Track on a box we pre-filled for them is a wasted step.
  useEffect(() => {
    const initial = waybillFromUrl();
    if (initial) void search(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={theme === 'dark'
      ? "min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-slate-100"
      : "min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50 text-slate-900"}>
      <header className={theme === 'dark' ? "border-b border-slate-700 bg-slate-900" : "border-b border-slate-200 bg-white"}>
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="bg-emerald-600 text-white p-2 rounded-lg"><Package size={20} /></div>
            <div>
              <div className={`font-bold ${theme === 'dark' ? 'text-slate-100' : 'text-slate-900'}`}>ShipRoute Logistics</div>
              <div className={`text-xs ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Track your shipment</div>
            </div>
          </div>
          <button
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            className={`p-2 rounded-lg transition ${theme === 'dark' ? 'bg-slate-800 hover:bg-slate-700 text-amber-300' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`}>
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 mb-6">
          <div className="grid md:grid-cols-[2fr_1fr_auto] gap-3 items-end">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">AWB / LR / Order Number *</label>
              <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && search()}
                placeholder="e.g. GNT/AWB/123456 or 24001234"
                className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 outline-none" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Mobile (optional)</label>
              <input value={mobile} onChange={(e) => setMobile(e.target.value)}
                placeholder="last 4 or full 10 digits"
                className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 outline-none" />
            </div>
            <button onClick={() => search()} disabled={busy}
              className="bg-emerald-600 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-60 flex items-center gap-2">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
              Track
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
            <AlertCircle size={20} className="text-amber-700 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">{error}</div>
          </div>
        )}

        {result && <Result result={result} initialMobile={mobile} />}
      </main>

      <footer className="text-center text-slate-400 text-xs py-6">© 2026 ShipRoute Logistics</footer>
    </div>
  );
}

function Result({ result, initialMobile }: { result: TrackResult; initialMobile: string }) {
  const o = result.order;
  const isDelivered = (o as any).isDelivered;
  const isRto = (o as any).isRto;
  // Blinking "current status" dot colour.
  const pingColor = isDelivered ? 'bg-emerald-400' : isRto ? 'bg-rose-400' : 'bg-blue-400';
  const solidColor = isDelivered ? 'bg-emerald-500' : isRto ? 'bg-rose-500' : 'bg-blue-500';

  // Route map (Origin → Customer). Origin pincode + customer pincode geocoded on the fly.
  const geo = useGeocode([o.originPincode, o.customerPincode]);
  const fromG = geo.get(o.originPincode);
  const toG = geo.get(o.customerPincode);
  const mapPoints: RoutePoint[] = [];
  if (fromG) mapPoints.push({
    lat: fromG.lat!, lng: fromG.lng!, kind: 'from',
    // Neutral labels, not `shipperName` / `customerName`. Hiding those in the card above while the
    // map marker still prints them would not be hiding them at all.
    title: 'Pickup / Origin',
    subtitle: [fromG.city, fromG.state].filter(Boolean).join(', ') || (o.originPincode ? `Pincode ${o.originPincode}` : undefined),
    lines: o.originPincode ? [`Origin pincode: ${o.originPincode}`] : [],
  });
  if (toG) mapPoints.push({
    lat: toG.lat!, lng: toG.lng!, kind: 'to',
    title: 'Delivery / Destination',
    subtitle: [o.customerCity, o.customerState, o.customerPincode].filter(Boolean).join(', ') || undefined,
    lines: [`Status: ${o.status}`],
  });
  const showMap = /^\d{6}$/.test(String(o.customerPincode ?? '')) || /^\d{6}$/.test(String(o.originPincode ?? ''));

  return (
    <div className="space-y-4">
      {/* HERO STATUS — big bold + last-event timestamp */}
      <div className={`rounded-2xl shadow-md border-2 p-6 text-center ${
        ((o as any).isDelivered) ? 'bg-emerald-50 border-emerald-300' :
        ((o as any).isRto) ? 'bg-rose-50 border-rose-300' :
        'bg-blue-50 border-blue-300'
      }`}>
        <div className="text-xs text-slate-500 uppercase tracking-wide mb-2 flex items-center justify-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${pingColor}`} />
            <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${solidColor}`} />
          </span>
          Current status
        </div>
        <div className={`text-3xl md:text-4xl font-extrabold tracking-tight ${
          ((o as any).isDelivered) ? 'text-emerald-700' :
          ((o as any).isRto) ? 'text-rose-700' :
          'text-blue-700'
        }`}>{o.status}</div>
        {result.events.length > 0 && (() => {
          const last = result.events[result.events.length - 1];
          return (
            <div className="text-sm text-slate-600 mt-2 flex items-center justify-center gap-2 flex-wrap">
              <Clock size={14} />
              <span className="font-medium">{new Date(last.eventDate).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}</span>
              {last.location && (<><span className="text-slate-400">·</span><MapPin size={14} /><span>{last.location}</span></>)}
            </div>
          );
        })()}
        {o.edd && !isDelivered && (
          <div className="text-xs text-slate-500 mt-2">Expected delivery: <b className="text-slate-700">{fmtDate(o.edd)}</b></div>
        )}
      </div>

      {/* SHIPMENT META */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            {/* No B2C/B2B tag and no customer name: the service type is our internal vocabulary and
                means nothing to the person tracking, and they already know who they are. The
                destination stays — it is how you confirm the parcel is heading to the right place. */}
            <div className="text-xs text-slate-500 uppercase tracking-wide">{o.awbNo ? 'AWB' : 'LR'} Number</div>
            <div className="text-xl font-bold text-slate-900 tabular-nums">{o.awbNo ?? o.lrNo ?? o.orderId}</div>
            <div className="text-sm text-slate-500 mt-1">{[o.customerCity, o.customerState].filter(Boolean).join(', ')} {o.customerPincode}</div>
          </div>
          {/* EDD — `Order.edd` holds whichever estimate we have: the courier's own expected-delivery
              date where it sent one (Delhivery B2B, Trackon push it on their tracking payload), or the
              one derived from booking date + TAT otherwise. Shown even AFTER delivery, next to the
              actual delivery date, because "did it arrive when you said" is the question people open
              this page to answer. */}
          {o.edd && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5">
              <div className="text-[11px] uppercase tracking-wide text-emerald-700 font-medium">Expected delivery</div>
              <div className="text-lg font-bold text-emerald-900 tabular-nums">{fmtDate(o.edd)}</div>
              {isDelivered && o.deliveredAt && (
                <div className="text-[11px] text-emerald-700 mt-0.5">Delivered on {fmtDate(o.deliveredAt)}</div>
              )}
            </div>
          )}
          {/* Courier only — the shipper's name is deliberately NOT shown. The carrier is what the
              customer needs (it is who is holding the parcel and who a query is about); which
              merchant sold it to them is the merchant's own relationship to disclose, not ours. */}
          {result.courier && (
            <div className="flex items-center gap-2.5">
              <Truck size={22} className="text-slate-400 shrink-0" />
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-500">Courier</div>
                <div className="text-lg font-bold text-slate-900 leading-tight">{result.courier.brand ?? result.courier.name}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {(o as any).isDelivered && (o.awbNo || o.lrNo) && (
        <PodSection awbNo={o.awbNo} lrNo={o.lrNo} initialMobile={initialMobile} />
      )}

      {/* ROUTE + TIMELINE side by side — map left, scans right. Collapses to one column below `lg`,
          and to the timeline alone when the shipment has no pincode we can plot. The map is sticky so
          it stays in view while a long scan list is scrolled beside it. */}
      <div className={showMap ? 'grid lg:grid-cols-2 gap-4 items-start' : ''}>
        {showMap && (
          <div className="lg:sticky lg:top-4">
            <RouteMapCard points={mapPoints} title="Shipment Route (From → To)" loading={geo.isLoading}
              unavailableText="Route map not available for this shipment" previewHeight={300} />
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
        <h2 className="text-base font-semibold text-slate-900 mb-3">Tracking timeline</h2>
        {result.events.length === 0 && <div className="text-sm text-slate-400">No tracking events yet</div>}
        <ol className="space-y-3">
          {result.events.map((e, idx) => (
            <li key={e.id} className="flex gap-3">
              <div className="flex flex-col items-center">
                {idx === result.events.length - 1 ? (
                  <span className="relative flex h-3.5 w-3.5" title="Current status">
                    <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${pingColor}`} />
                    <span className={`relative inline-flex rounded-full h-3.5 w-3.5 ${solidColor}`} />
                  </span>
                ) : (
                  <div className="w-3 h-3 rounded-full bg-slate-300" />
                )}
                {idx < result.events.length - 1 && <div className="w-px flex-1 bg-slate-200 my-1" />}
              </div>
              <div className="pb-3 flex-1 min-w-0">
                <div>
                  <span className={`inline-block font-semibold text-sm px-2 py-0.5 rounded ${eventTone(e)}`}>
                    {e.description ?? e.status ?? e.courierStatusCode ?? 'Update'}
                  </span>
                </div>
                <div className="text-xs text-slate-600 flex items-center gap-1.5 mt-1">
                  <Clock size={11} className="text-slate-400" />
                  <span className="font-medium">{new Date(e.eventDate).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}</span>
                </div>
                {(e.city || e.location) && (
                  <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                    <MapPin size={11} className="text-slate-400" />
                    <span>{e.city ?? e.location}{e.state ? ', ' + e.state : ''}</span>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>
        </div>
      </div>
    </div>
  );
}

function PodSection({ awbNo, lrNo, initialMobile }: { awbNo?: string; lrNo?: string; initialMobile: string }) {
  const [mobile, setMobile] = useState(initialMobile);
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState<'idle' | 'sent' | 'verified'>('idle');
  const [busy, setBusy] = useState(false);
  const [podUrl, setPodUrl] = useState<string | null>(null);
  const [maskedMobile, setMaskedMobile] = useState<string | null>(null);
  const [debugOtp, setDebugOtp] = useState<string | null>(null);

  const requestOtp = async () => {
    if (!mobile) return toast.error('Enter your mobile to receive OTP');
    setBusy(true);
    try {
      const r = await api.post('/public/track/pod/request-otp', { awbNo, lrNo, mobile });
      setStep('sent');
      setMaskedMobile(r.data.mobile);
      if (r.data.debugOtp) setDebugOtp(r.data.debugOtp);
      toast.success(r.data.sent ? 'OTP sent to your mobile' : 'OTP generated (SMS not configured)');
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Could not send OTP');
    } finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    if (!otp) return toast.error('Enter the OTP');
    setBusy(true);
    try {
      const r = await api.post('/public/track/pod/verify-otp', { awbNo, lrNo, mobile, otp });
      setPodUrl(r.data.podUrl);
      setStep('verified');
      toast.success('Verified — POD ready');
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Invalid OTP');
    } finally { setBusy(false); }
  };

  if (step === 'verified' && podUrl) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5">
        <div className="flex items-center gap-2 text-emerald-800 mb-3">
          <CheckCircle2 size={18} /> <b>POD verified</b>
        </div>
        <a href={podUrl} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-2 bg-emerald-700 text-white px-4 py-2 rounded-lg font-medium hover:bg-emerald-800">
          <Download size={16} /> Download POD
        </a>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5">
      <div className="flex items-center gap-2 mb-3">
        <Lock size={16} className="text-amber-600" />
        <h2 className="text-base font-semibold text-slate-900">Download Proof of Delivery (POD)</h2>
      </div>
      {step === 'idle' && (
        <div className="grid md:grid-cols-[1fr_auto] gap-3">
          <input value={mobile} onChange={(e) => setMobile(e.target.value)}
            placeholder="Enter the mobile used on this shipment"
            className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 outline-none" />
          <button onClick={requestOtp} disabled={busy}
            className="bg-emerald-600 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-60 flex items-center gap-2">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Smartphone size={16} />}
            Send OTP
          </button>
        </div>
      )}
      {step === 'sent' && (
        <div>
          <div className="text-sm text-slate-600 mb-3">OTP sent to <b className="text-slate-900">{maskedMobile}</b>. Valid for 10 minutes.</div>
          {debugOtp && <div className="text-xs text-amber-700 mb-2">Dev OTP: <code className="bg-amber-50 px-1.5 py-0.5 rounded font-mono">{debugOtp}</code> (SMS not configured)</div>}
          <div className="grid md:grid-cols-[1fr_auto_auto] gap-3">
            <input value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="6-digit OTP" maxLength={6}
              className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100 outline-none tabular-nums tracking-widest text-center" />
            <button onClick={verifyOtp} disabled={busy}
              className="bg-emerald-600 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin inline" /> : 'Verify'}
            </button>
            <button onClick={() => { setStep('idle'); setOtp(''); setDebugOtp(null); }}
              className="px-3 py-2.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Resend</button>
          </div>
        </div>
      )}
    </div>
  );
}


export function TrackApp() {
  return (
    <Routes>
      {/* One screen on every path. `/*` rather than `/:awbNo` so a legacy path link carrying an LR
          with slashes (`/GNT/AWB/123456`) still lands here instead of falling through to no match —
          TrackHome reads the waybill off the URL itself. */}
      <Route path="/*" element={<TrackHome />} />
    </Routes>
  );
}
